import { Injectable } from "@angular/core";
import { HttpClient, HttpEvent, HttpEventType, HttpParams } from "@angular/common/http";
import { Observable, BehaviorSubject } from "rxjs";
import { timeout, catchError } from "rxjs/operators";
import { environment } from "../../../environments/environment";

export interface ArchivoCargadoResponse {
  message: string;
  url: string;
  codigo: number;
}

@Injectable({
  providedIn: "root",
})
export class DigitalesService {
  private apiUrl = `${environment.apiUrl}/api`;
  private readonly requestTimeoutMs = 45000;

  // 🔹 Observable para notificar progreso de carga
  private uploadProgressSubject = new BehaviorSubject<number>(0);
  public uploadProgress$ = this.uploadProgressSubject.asObservable();

  constructor(private http: HttpClient) {}

  // ===========================
  // 📤 MÉTODOS DE CARGA
  // ===========================

  subirArchivo(
    file: File,
    id: string | number,
    nombreCarpeta: string,
    nombreArchivo: string,
    prefijo: string,
  ): Observable<HttpEvent<ArchivoCargadoResponse>> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("nombreArchivo", nombreArchivo);
    formData.append("prefijo", prefijo);
    formData.append("id", id.toString());

    const url = `${this.apiUrl}/upload/${nombreCarpeta}/${id}`;
      console.log("Subiendo archivo a URL:", url);
    return this.http.post<ArchivoCargadoResponse>(url, formData, {
      reportProgress: true,
      withCredentials: true, // ✅ Enviar cookies seguras
      observe: "events",
    });
  }

  subirArchivoVehiculos(
    file: File,
    id: string | number,
    nombreCarpeta: string,
    nombreArchivo: string,
    prefijo: string,
  ): Observable<HttpEvent<ArchivoCargadoResponse>> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("nombreArchivo", nombreArchivo);
    formData.append("prefijo", prefijo);
    formData.append("id", id.toString());

    const url = `${this.apiUrl}/upload/${nombreCarpeta}`;
      console.log("Subiendo archivo a URL:", url);
    return this.http.post<ArchivoCargadoResponse>(url, formData, {
      reportProgress: true,
      withCredentials: true, // ✅ Enviar cookies seguras
      observe: "events",
    });
  }

  descargarArchivoDesdeUrl(url: string): Observable<HttpEvent<Blob>> {
    return this.http
      .get(url, {
        withCredentials: true,
        responseType: "blob",
        observe: "events",
      })
      .pipe(timeout(this.requestTimeoutMs));
  }

  uploadFile(file: File, folder: string): Observable<HttpEvent<any>> {
    const formData = new FormData();
    formData.append("file", file);

    const url = this.apiUrl + folder;

    return this.http.post<any>(url, formData, {
      reportProgress: true,
      withCredentials: true, // ✅ Enviar cookies seguras
      observe: "events",
    });
  }

  // ===========================
  // 🔗 MÉTODOS DE DESCARGA
  // ===========================

  /**
   * Obtener URL firmada
   * @param filePath Ruta del archivo
   */
  getSignedUrl(filePath: string) {
    return this.http.get<{ url: string }>(
      `${this.apiUrl}/image-url/${filePath}`,
      { withCredentials: true },
    );
  }

  uploadMultiple(files: File[], folder: string): Observable<HttpEvent<any>> {
    const formData = new FormData();

    files.forEach((file) => formData.append("files[]", file));

    const url = `${this.apiUrl}/upload-multiple/${folder}`;

    return this.http.post<HttpEvent<any>>(url, formData, {
      reportProgress: true,
      withCredentials: true,
      observe: "events",
    });
  }

  // ===========================
  // 🖊️ MÉTODOS PARA FIRMAS
  // ===========================
  /**
   * 🗑️ Eliminar archivo de la carpeta especificada
   */
 /*  eliminarArchivo(rutaCompleta: string): Observable<any> {
    const url = `${this.apiUrl}/delete-file`;
    
    return this.http.post(url, { rutaCompleta }, {
      withCredentials: true
    });
  } */


  /**
   * Obtener firmas por NIT y cédula
   * @param nit NIT de la empresa
   * @param cedula Cédula de la persona
   */
  obtenerFirmas(nit: string, cedula: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/firmas/${nit}/${cedula}`, {
      withCredentials: true,
    });
  }

  /**
   * Obtener firma específica
   * @param nit NIT de la empresa
   * @param cedula Cédula de la persona
   * @param firmaId ID de la firma
   */
  obtenerFirmaById(
    nit: string,
    cedula: string,
    firmaId: string,
  ): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/firmas/${nit}/${cedula}/${firmaId}`, {
      responseType: "blob",
      withCredentials: true,
    });
  }

  /**
   * Subir firma
   * @param file Archivo de firma
   * @param nit NIT de la empresa
   * @param cedula Cédula de la persona
   */
  subirFirma(
    file: File,
    nit: string,
    cedula: string,
  ): Observable<HttpEvent<any>> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("nit", nit);
    formData.append("cedula", cedula);

    return this.http.post<any>(
      `${this.apiUrl}/firmas/upload/${nit}/${cedula}`,
      formData,
      {
        reportProgress: true,
        withCredentials: true,
        observe: "events",
      },
    );
  }

  /**
   * Eliminar firma
   * @param nit NIT de la empresa
   * @param cedula Cédula de la persona
   * @param firmaId ID de la firma
   */
  eliminarFirma(nit: string, cedula: string, firmaId: string): Observable<any> {
    return this.http.delete(
      `${this.apiUrl}/firmas/${nit}/${cedula}/${firmaId}`,
      { withCredentials: true },
    );
  }

  /**
   * Activar firma como firma actual
   * @param nit NIT de la empresa
   * @param cedula Cédula de la persona
   * @param firmaId ID de la firma
   */
  activarFirma(nit: string, cedula: string, firmaId: string): Observable<any> {
    return this.http.patch(
      `${this.apiUrl}/firmas/${nit}/${cedula}/${firmaId}/activate`,
      {},
      { withCredentials: true },
    );
  }

  // ===========================
  // 📁 MÉTODOS PARA DOCUMENTOS
  // ===========================
  listarDocumentos(carpeta: string): Observable<any> {
    const carpetaCodificada = encodeURIComponent(carpeta);
    const url = `${this.apiUrl}/documentos/${carpetaCodificada}/listar`;
    return this.http.get(url, { withCredentials: true });
  }

  listarDocumentosPesvCargados(nit: string, carpeta: string): Observable<any> {
    const ruta = `${nit}/pesv/cargados/${carpeta}`;
    return this.listarDocumentos(ruta);
  }

  /**
   * 🔹 Descargar documento de GCP
   */
  descargarDocumento3(
    carpeta: string,
    nombreArchivo: string,
    formatoId: number = 3,
    numeroComite?: number,
  ): Observable<Blob> {
    const carpetaCodificada = encodeURIComponent(carpeta);
    const params = new URLSearchParams({
      archivo: nombreArchivo,
      formato_id: String(formatoId),
      tipo: "pdf",
    });

    if (numeroComite !== undefined && numeroComite !== null) {
      params.set("numeroComite", String(numeroComite));
    }

    const url = `${this.apiUrl}/documentos/${carpetaCodificada}/descargar?${params.toString()}`;
    return this.http
      .get(url, {
        withCredentials: true,
        responseType: "blob",
      })
      .pipe(timeout(this.requestTimeoutMs));
  }

  descargarDocumentoPesvCargado(
    nit: string,
    carpeta: string,
    nombreArchivo: string,
    formatoId: number = 3,
    numeroComite?: number,
  ): Observable<Blob> {
    const ruta = `${nit}/pesv/cargados/${carpeta}`;
    return this.descargarDocumento3(
      ruta,
      nombreArchivo,
      formatoId,
      numeroComite,
    );
  }

  descargarDocumento4(
    carpeta: string,
    nombreArchivo: string,
  ): Observable<Blob> {
    const carpetaCodificada = encodeURIComponent(carpeta);
    // Descargar documento por carpeta usando el endpoint que ya funciona en el backend
    // Ejemplo: http://localhost:3000/api/documentos-versiones/descargar/pesv/LID/
    // El backend se encarga de resolver el archivo dentro de esa carpeta.
    const url = `${this.apiUrl}/documentos-versiones/descargar/${carpetaCodificada}/?tipo=pdf`;
    return this.http
      .get(url, {
        withCredentials: true,
        responseType: "blob",
      })
      .pipe(timeout(this.requestTimeoutMs));
  }

  descargarDocumento4ConRespuesta(
    carpeta: string,
    formatoId: number = 3,
    numeroComite?: number,
    nombreArchivo?: string,
    liderId?: number | string,
    tipoSalida?: "word" | "pdf",
    numeroActa?: string | number | null,
  ): Observable<any> {
    const carpetaCodificada = encodeURIComponent(carpeta);
    const params = new URLSearchParams({
      formato_id: String(formatoId),
      tipo: "pdf",
    });

    if (numeroComite !== undefined && numeroComite !== null) {
      params.set("numeroComite", String(numeroComite));
    }

    if (nombreArchivo) {
      params.set("archivo", nombreArchivo);
    }

    if (
      liderId !== undefined &&
      liderId !== null &&
      String(liderId).trim() !== ""
    ) {
      params.set("lider_id", String(liderId));
    }

    if (numeroActa !== undefined && numeroActa !== null) {
      params.set("numeroActa", String(numeroActa));
    }

    params.set("tipo", "pdf");

    const url = `${this.apiUrl}/documentos-versiones/descargar/${carpetaCodificada}/?${params.toString()}`;
    // Debug: log constructed URL for troubleshooting
    try {
      console.debug(
        "[DigitalesService] descargarDocumento4ConRespuesta URL ->",
        url,
      );
    } catch (e) {}

    return this.http
      .get(url, {
        withCredentials: true,
        responseType: "blob",
        observe: "response",
      })
      .pipe(timeout(this.requestTimeoutMs));
  }

  /**
   * 🗑️ Eliminar archivo de la carpeta especificada
   */
  eliminarArchivo(rutaCompleta: string): Observable<any> {
    const url = `${this.apiUrl}/delete-file`;

    return this.http.post(
      url,
      { rutaCompleta },
      {
        withCredentials: true,
      },
    );
  }

  /**
   * Eliminar múltiples archivos
   * @param rutas Array de rutas completas
   */
  eliminarMultiples(rutas: string[]): Observable<any> {
    const url = `${this.apiUrl}/delete-files`;

    return this.http.post(
      url,
      { rutas },
      {
        withCredentials: true,
      },
    );
  }
subirDocumentoSiniestro(
  file: File,
  idSiniestro: number,
  carpeta: string,
  index: number,
  prefijo: string = 'soporte'
): Observable<HttpEvent<any>> {
  const formData = new FormData();
  const nombreArchivo = `${idSiniestro}_${index + 1}`;

  formData.append('file', file);
  formData.append('idSiniestro', idSiniestro.toString());
  formData.append('nombreArchivo', nombreArchivo);
  formData.append('prefijo', prefijo);

  // ✅ Usamos la nueva ruta
  const url = `${this.apiUrl}/upload-siniestro/${carpeta}`;

  return this.http.post<HttpEvent<any>>(url, formData, {
    reportProgress: true,
    withCredentials: true,
    observe: 'events'
  });
}


  /**
   * 🗑️ Eliminar documento
   */
  eliminarDocumento(rutaCompleta: string): Observable<any> {
    const url = `${this.apiUrl}/delete-file`;
    return this.http.post(url, { rutaCompleta }, { withCredentials: true });
  }

  getDocumentosSiniestro(carpeta: string): Observable<any> {
  const url = `${this.apiUrl}/list-siniestro-files/${carpeta}`;
  return this.http.get(url, { withCredentials: true });
}

  // ===========================
  // 🔧 MÉTODOS AUXILIARES
  // ===========================

  /**
   * Obtener progreso de carga
   */
  getUploadProgress(): number {
    return this.uploadProgressSubject.value;
  }

  /**
   * Actualizar progreso de carga
   */
  setUploadProgress(progress: number): void {
    this.uploadProgressSubject.next(progress);
  }

  /**
   * Obtener extensión de archivo
   */
  getFileExtension(fileName: string): string {
    const parts = fileName.split(".");
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
  }

  /**
   * Validar tipo de archivo
   */
  isValidFileType(fileName: string, allowedExtensions: string[]): boolean {
    const ext = this.getFileExtension(fileName);
    return allowedExtensions.includes(ext);
  }

  /**
   * Obtener tamaño legible de archivo
   */
  getReadableFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  }

  /**
   *
   * @param nitOId NIT de la empresa o ID del documento (como string)
   * @param codigoDocumento Código del documento (ej: "001", "PR-PESV-01")
   * @param tipoDocumental Nombre completo del tipo jerárquico (ej: "formato", "politica")
   * @param numeroVersion Número de versión (ej: "003", "004", "030")
   *
   */
  descargarArchivoDeVersion(
    nitOId: string,
    codigoDocumento: string,
    tipoDocumental: string,
    numeroVersion: string,
  ): Observable<any> {
    // Agregar "v" al número de versión si no lo tiene
    const versionConV = numeroVersion.startsWith("v")
      ? numeroVersion
      : `v${numeroVersion}`;

    // Construir la ruta completa: pesv/{NIT}/implementacion/{CODIGO}/{TIPO}/{VERSION}
    const rutaCompleta = `${nitOId}/pesv/documental/${encodeURIComponent(tipoDocumental)}/${encodeURIComponent(codigoDocumento)}/${versionConV}`;
    const rutaCodificada = encodeURIComponent(rutaCompleta);
    const url = `${this.apiUrl}/documentos-versiones/descargar/${rutaCodificada}/?tipo=pdf`;

    return this.http
      .get(url, {
        withCredentials: true,
        responseType: "blob",
        observe: "response", // ✅ Obtener respuesta completa con headers
      })
      .pipe(timeout(this.requestTimeoutMs));
  }

  /**
   * Descargar archivo usando el ID numérico de la versión en la base de datos.
   * Endpoint esperado: GET {environment.apiUrl}/api/versiones-documento/{id}/descargar/
   */
  descargarArchivoPorVersionId(
    versionId: number,
    numeroComiteOtipoSalida?: number | "pdf" | "word",
    tipoSalida?: "pdf" | "word",
    numeroActa?: string | number | null,
  ): Observable<any> {
    let params = new HttpParams();

    const numeroComite =
      typeof numeroComiteOtipoSalida === "number"
        ? numeroComiteOtipoSalida
        : undefined;

    const tipoSalidaResuelta =
      typeof numeroComiteOtipoSalida === "string"
        ? numeroComiteOtipoSalida
        : tipoSalida;

    if (numeroComite !== undefined && numeroComite !== null) {
      params = params.set("numeroComite", String(numeroComite));
    }

    if (numeroActa !== undefined && numeroActa !== null) {
      params = params.set("numeroActa", String(numeroActa));
    }

    params = params.set("tipo", tipoSalidaResuelta || "pdf");

    const url = `${this.apiUrl}/versiones-documento/${versionId}/descargar`;

    console.log("📥 URL descarga:", url);

    return this.http
      .get(url, {
        withCredentials: true,
        params,
        responseType: "blob",
        observe: "response",
      })
      .pipe(timeout(this.requestTimeoutMs));
  }

  /**
   * Intenta descargar archivo de una versión específica.
   * Si NO ENCUENTRA archivo en esa ruta, fallback a DigitalesPesv usando formatosService.
   * Útil cuando no hay documento cargado en la versión.
   *
   * @param nitOId NIT empresa
   * @param codigoDocumento Código documento
   * @param tipoDocumental Tipo documental
   * @param numeroVersion Número versión
   * @param digitalesPesvId ID del documento default en DigitalesPesv (ej: 3 para Política)
   * @param formatosService Inyectar FormatosService para fallback
   */
  descargarArchivoDeVersionConFallback(
    nitOId: string,
    codigoDocumento: string,
    tipoDocumental: string,
    numeroVersion: string,
    digitalesPesvId: number,
    formatosService: any, // FormatosService
    datosAdicionales: any = {},
  ): Observable<any> {
    return this.descargarArchivoDeVersion(
      nitOId,
      codigoDocumento,
      tipoDocumental,
      numeroVersion,
    ).pipe(
      catchError((err: any) => {
        // Si falla (404, no encontrado, etc.), hacer fallback a DigitalesPesv
        console.warn(
          `⚠️ No se encontró documento en versión ${numeroVersion}, haciendo fallback a DigitalesPesv ID=${digitalesPesvId}`,
          err,
        );
        return formatosService.generarDocumentoPdf(
          digitalesPesvId,
          datosAdicionales,
        );
      }),
    );
  }

  descargarDesdeDocumento(urlArchivo: string): Observable<Blob> {
    return this.http
      .get(urlArchivo, {
        responseType: "blob",
      })
      .pipe(timeout(this.requestTimeoutMs));
  }

  uploadFilePlanAccion(file: File, folder: string, prefijo: string): Observable<HttpEvent<any>> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('prefijo', prefijo);

  const url = this.apiUrl + folder;

  return this.http.post<any>(url, formData, {
    reportProgress: true,
    withCredentials: true,
    observe: 'events'
  });
}

getDocumentosPlanAccion(carpeta: string) {
  return this.http.get<{ message: string; archivos: any[] }>(
    `${this.apiUrl}/list-siniestro-files/${carpeta}`,
    { withCredentials: true }
  );
}

subirDocumentoPlanAccion(
  file: File,
  carpeta: string,
  nombreArchivo: string,
  prefijo: string
): Observable<HttpEvent<any>> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('nombreArchivo', nombreArchivo);
  formData.append('prefijo', prefijo);

  const url = `${this.apiUrl}/upload-siniestro/${carpeta}`;

  return this.http.post<any>(url, formData, {
    reportProgress: true,
    withCredentials: true,
    observe: 'events'
  });
}

}

